import type { Dispatch, MutableRefObject } from 'react';
import type { TFunction } from 'i18next';
import type { IClientPublishOptions } from 'mqtt';

import { decodeMqttText } from '@/contexts/mqtt/payload';

import type { WinchMqttUser } from '../winchMqttApi';
import {
    buildGetPositionPayload,
    parseMqttCommandResponse,
    parseVerticalScan,
    parseWinchGetPosition,
    parseWinchOnlinePayload,
    parseWinchStatusSnapshot,
    parseWinchTelemetry,
    winchMqttTopics,
} from '../winchMqttApi';

import type { CageWinchMqttAction, CageWinchMqttState } from './cageWinchMqttState';
import {
    limitsOnlySnapshot,
    presetsOnlySnapshot,
    scanUpdateSource,
    toastWinchCommand,
    withMeasurementPosition,
} from './winchMqttMessageHelpers';
import { showWinchToast } from './winchMqttToasts';

const WINCH_MQTT_LOG = '[winch-mqtt]';
const DEBUG = false;

function logWinchMqttResponse(label: string, parsed: unknown): void {
    if (DEBUG) {
        console.log(WINCH_MQTT_LOG, label, parsed);
    }
}

export type WinchMqttPublish = (
    topic: string,
    payload: string,
    options?: IClientPublishOptions
) => void;

export type PendingMeasurementSave = {
    distance: number;
    depth: number;
    uuid: string;
};

export type WinchMqttMessageDeps = {
    getWinchId: () => number | null;
    getUser: () => WinchMqttUser;
    getState: () => CageWinchMqttState;
    dispatch: Dispatch<CageWinchMqttAction>;
    publish: WinchMqttPublish;
    t: TFunction;
    pendingMeasurementRef: MutableRefObject<PendingMeasurementSave | null>;
    onSetPositionSettled: (failed: boolean) => void;
    onSetLimitsSettled: (failed: boolean, requestId?: string | null) => boolean;
    /** Called with the request UUID of every scan enable or scan status reply. */
    onScanReply: (requestId: string | undefined, scanRunning: boolean) => void;
    requestScanStatus: () => void;
};

export function handleWinchMqttMessage(
    deps: WinchMqttMessageDeps,
    topic: string,
    message: Uint8Array
): void {
    const winchId = deps.getWinchId();
    if (winchId == null) {
        return;
    }
    const topics = winchMqttTopics(winchId);
    const subscribed = new Set(Object.values(topics.subscribe));
    if (!subscribed.has(topic)) {
        return;
    }

    if (topic === topics.subscribe.online) {
        const isOnline = parseWinchOnlinePayload(message);
        if (isOnline !== null) {
            deps.dispatch({ type: 'SET_ONLINE', payload: isOnline });
        }
        return;
    }

    try {
        const parsed: unknown = JSON.parse(decodeMqttText(message));

        if (topic === topics.subscribe.telemetry) {
            const telemetry = parseWinchTelemetry(parsed, deps.getState().telemetry?.state);
            if (telemetry) {
                deps.dispatch({ type: 'SET_TELEMETRY', payload: telemetry });
                deps.dispatch({ type: 'SET_ONLINE', payload: true });
            }
            return;
        }
        if (topic === topics.subscribe.getPosition) {
            logWinchMqttResponse('getposition/response', parsed);
            const config = parseWinchGetPosition(parsed);
            if (config) {
                deps.dispatch({
                    type: 'SET_CONFIG',
                    payload: presetsOnlySnapshot(config),
                    replacePositions: true,
                });
                deps.dispatch({ type: 'SET_ONLINE', payload: true });
            }
            return;
        }
        if (topic === topics.subscribe.status) {
            logWinchMqttResponse('status/response', parsed);
            const snapshot = parseWinchStatusSnapshot(parsed);
            if (snapshot && (snapshot.limits != null || snapshot.speed != null)) {
                deps.dispatch({ type: 'SET_CONFIG', payload: limitsOnlySnapshot(snapshot) });
                deps.dispatch({ type: 'SET_ONLINE', payload: true });
            }
            return;
        }
        if (topic === topics.subscribe.setPosition) {
            const result = parseMqttCommandResponse(parsed);
            const pending = deps.pendingMeasurementRef.current;
            if (pending == null || result.uuid == null || result.uuid !== pending.uuid) {
                return;
            }
            deps.onSetPositionSettled(!result.success);
            if (result.success) {
                deps.dispatch({
                    type: 'SET_CONFIG',
                    payload: withMeasurementPosition(deps.getState().config, {
                        distance: pending.distance,
                        depth: pending.depth,
                    }),
                });
                deps.publish(topics.publish.getPosition, buildGetPositionPayload(deps.getUser()), {
                    qos: 1,
                });
            }
            deps.pendingMeasurementRef.current = null;
            toastWinchCommand(
                deps.t,
                result.success,
                'winchControl.toast.positionSaved',
                'winchControl.toast.positionSaveError',
                'winch-setposition',
                'winch-setposition-error'
            );
            return;
        }
        if (topic === topics.subscribe.goto) {
            const result = parseMqttCommandResponse(parsed);
            toastWinchCommand(
                deps.t,
                result.success,
                'winchControl.toast.moveInitiated',
                'winchControl.toast.moveError',
                'winch-move',
                'winch-move-error',
                // The move has started, not finished.
                'info'
            );
            return;
        }
        if (topic === topics.subscribe.stop) {
            const result = parseMqttCommandResponse(parsed);
            toastWinchCommand(
                deps.t,
                result.success,
                'winchControl.toast.stopped',
                'winchControl.toast.stopError',
                'winch-stop',
                'winch-stop-error'
            );
            return;
        }
        if (topic === topics.subscribe.verticalScanStatus) {
            logWinchMqttResponse('vertical_scan/status/response', parsed);
            const scan = parseVerticalScan(parsed);
            if (scan) {
                deps.dispatch({ type: 'SET_VERTICAL_SCAN', payload: scan, source: 'status' });
            }
            deps.onScanReply(parseMqttCommandResponse(parsed).uuid, scan?.enabled === true);
            return;
        }
        if (topic === topics.subscribe.verticalScanDisable) {
            const result = parseMqttCommandResponse(parsed);
            if (result.success) {
                const scan = parseVerticalScan(parsed);
                const current = deps.getState().verticalScan;
                const stopped = scan
                    ? { ...scan, enabled: false }
                    : current
                      ? { ...current, enabled: false }
                      : null;
                if (stopped) {
                    deps.dispatch({
                        type: 'SET_VERTICAL_SCAN',
                        payload: stopped,
                        source: 'disable',
                    });
                }
            }
            toastWinchCommand(
                deps.t,
                result.success,
                'winchControl.toast.scanUpdated',
                'winchControl.toast.scanError',
                'winch-scan',
                'winch-scan-error'
            );
            return;
        }
        if (
            topic === topics.subscribe.verticalScanEnable ||
            topic === topics.subscribe.verticalScanSetLimits
        ) {
            const result = parseMqttCommandResponse(parsed);
            const isSetLimits = topic === topics.subscribe.verticalScanSetLimits;
            if (isSetLimits && !deps.onSetLimitsSettled(!result.success, result.uuid)) {
                return;
            }
            const scan = parseVerticalScan(parsed);
            if (scan && (!isSetLimits || result.success)) {
                deps.dispatch({
                    type: 'SET_VERTICAL_SCAN',
                    payload: scan,
                    source: scanUpdateSource(topic, topics),
                });
            } else if (!isSetLimits) {
                deps.requestScanStatus();
            }
            if (!isSetLimits) {
                deps.onScanReply(result.uuid, scan?.enabled === true);
            }
            toastWinchCommand(
                deps.t,
                result.success,
                'winchControl.toast.scanUpdated',
                'winchControl.toast.scanError',
                'winch-scan',
                'winch-scan-error'
            );
        }
    } catch (error) {
        console.error(WINCH_MQTT_LOG, topic, error);
        if (topic === topics.subscribe.setPosition) {
            deps.onSetPositionSettled(true);
            deps.pendingMeasurementRef.current = null;
        }
        const isCommandResponse =
            topic === topics.subscribe.goto ||
            topic === topics.subscribe.stop ||
            topic === topics.subscribe.setPosition ||
            topic === topics.subscribe.getPosition ||
            topic === topics.subscribe.status ||
            topic === topics.subscribe.verticalScanEnable ||
            topic === topics.subscribe.verticalScanDisable ||
            topic === topics.subscribe.verticalScanSetLimits ||
            topic === topics.subscribe.verticalScanStatus;
        if (isCommandResponse) {
            showWinchToast(deps.t('winchControl.toast.parseError'), 'error', 'winch-error');
        }
    }
}
